import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    Plus, X, Save, Info, AlertTriangle, CheckCircle2, Trash2, Activity, Beaker,
} from 'lucide-react';
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip as RechartsTooltip, ReferenceLine, Scatter, ComposedChart,
} from 'recharts';
import useLabPermissions from './useLabPermissions';
import { useQCData } from './useQCData';
import {
    evaluateWestgardRules, WESTGARD_RULE_LABELS, REJECTING_RULES,
    type QCSample,
} from './qcUtils';

// ─── Shared bits (mirrors the Modal/Field/inputCls pattern used across Lab files) ─────
const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode; width?: string }> = ({
    title, onClose, children, width = 'max-w-md',
}) => createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}>
        <div className={`w-full ${width} rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] shadow-2xl flex flex-col max-h-[90vh]`}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--v2-border-subtle)]">
                <h2 className="text-[14px] font-bold text-[var(--v2-text-heading)] m-0">{title}</h2>
                <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer text-[var(--v2-text-muted)] hover:text-[var(--v2-text-primary)] hover:bg-[var(--v2-bg-surface)] transition-colors border-0 bg-transparent">
                    <X className="w-4 h-4" strokeWidth={2} />
                </button>
            </div>
            <div className="overflow-y-auto flex-1 px-5 py-4">{children}</div>
        </div>
    </div>,
    document.body
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="flex flex-col gap-1">
        <label className="text-[10px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)]">{label}</label>
        {children}
    </div>
);
const inputCls = "w-full rounded-xl border border-[var(--v2-border-interactive)] bg-[var(--v2-bg-surface)] px-3 py-2 text-[13px] text-[var(--v2-text-primary)] outline-none transition-colors";

// ─── Add Instrument Modal ───────────────────────────────────────────────────────
const AddInstrumentModal: React.FC<{ onSave: (data: { name: string; manufacturer: string; model: string; department: string }) => void; onClose: () => void }> = ({ onSave, onClose }) => {
    const [name, setName] = useState('');
    const [manufacturer, setManufacturer] = useState('');
    const [model, setModel] = useState('');
    const [department, setDepartment] = useState('');
    return (
        <Modal title="Add Instrument" onClose={onClose}>
            <div className="flex flex-col gap-3">
                <Field label="Instrument Name"><input className={inputCls} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Sysmex XN-1000" /></Field>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Manufacturer"><input className={inputCls} value={manufacturer} onChange={e => setManufacturer(e.target.value)} placeholder="Sysmex" /></Field>
                    <Field label="Model"><input className={inputCls} value={model} onChange={e => setModel(e.target.value)} placeholder="XN-1000" /></Field>
                </div>
                <Field label="Department"><input className={inputCls} value={department} onChange={e => setDepartment(e.target.value)} placeholder="Hematology" /></Field>
                <div className="flex gap-2 justify-end pt-1">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] bg-[var(--v2-bg-surface)]">Cancel</button>
                    <button type="button" disabled={!name.trim()} onClick={() => onSave({ name: name.trim(), manufacturer, model, department })}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer text-white disabled:opacity-50 border-0" style={{ background: '#059669' }}>
                        <Save className="w-3.5 h-3.5" strokeWidth={2} /> Add
                    </button>
                </div>
            </div>
        </Modal>
    );
};

// ─── Add QC Sample/Level Modal ──────────────────────────────────────────────────
const AddQCSampleModal: React.FC<{ onSave: (data: { testName: string; level: string; unit: string; targetMean: number; targetSD: number }) => void; onClose: () => void }> = ({ onSave, onClose }) => {
    const [testName, setTestName] = useState('');
    const [level, setLevel] = useState('Level 1');
    const [unit, setUnit] = useState('');
    const [targetMean, setTargetMean] = useState('');
    const [targetSD, setTargetSD] = useState('');
    const valid = testName.trim() && targetMean !== '' && targetSD !== '' && Number(targetSD) > 0;
    return (
        <Modal title="Add QC Sample / Level" onClose={onClose}>
            <div className="flex flex-col gap-3">
                <Field label="Test Name"><input className={inputCls} value={testName} onChange={e => setTestName(e.target.value)} placeholder="e.g. Hemoglobin" /></Field>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Control Level">
                        <select className={inputCls} value={level} onChange={e => setLevel(e.target.value)}>
                            {['Level 1', 'Level 2', 'Level 3'].map(l => <option key={l}>{l}</option>)}
                        </select>
                    </Field>
                    <Field label="Unit"><input className={inputCls} value={unit} onChange={e => setUnit(e.target.value)} placeholder="g/dL" /></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Target Mean"><input type="number" className={inputCls} value={targetMean} onChange={e => setTargetMean(e.target.value)} placeholder="14.0" /></Field>
                    <Field label="Target SD"><input type="number" className={inputCls} value={targetSD} onChange={e => setTargetSD(e.target.value)} placeholder="0.4" /></Field>
                </div>
                <div className="flex gap-2 justify-end pt-1">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer border border-[var(--v2-border-card)] text-[var(--v2-text-muted)] bg-[var(--v2-bg-surface)]">Cancel</button>
                    <button type="button" disabled={!valid}
                        onClick={() => onSave({ testName: testName.trim(), level, unit, targetMean: Number(targetMean), targetSD: Number(targetSD) })}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-bold cursor-pointer text-white disabled:opacity-50 border-0" style={{ background: '#059669' }}>
                        <Save className="w-3.5 h-3.5" strokeWidth={2} /> Add
                    </button>
                </div>
            </div>
        </Modal>
    );
};

// ─── Levey-Jennings Chart ────────────────────────────────────────────────────────
const LeveyJenningsChart: React.FC<{ sample: QCSample; evaluations: ReturnType<typeof evaluateWestgardRules> }> = ({ sample, evaluations }) => {
    const data = evaluations.map(e => ({
        date: new Date(e.run.timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
        value: e.run.value,
        outOfControl: e.outOfControl,
    }));
    const { targetMean: mean, targetSD: sd } = sample;
    return (
        <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--v2-border-subtle)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="var(--v2-text-muted)" />
                    <YAxis tick={{ fontSize: 10 }} stroke="var(--v2-text-muted)" domain={[mean - 4 * sd, mean + 4 * sd]} />
                    <RechartsTooltip contentStyle={{ background: 'var(--v2-bg-card)', border: '1px solid var(--v2-border-card)', borderRadius: 8, fontSize: 11 }} />
                    <ReferenceLine y={mean} stroke="#059669" strokeWidth={1.5} label={{ value: 'Mean', fontSize: 9, fill: '#059669' }} />
                    <ReferenceLine y={mean + sd} stroke="#D97706" strokeDasharray="4 2" label={{ value: '+1SD', fontSize: 9, fill: '#D97706' }} />
                    <ReferenceLine y={mean - sd} stroke="#D97706" strokeDasharray="4 2" label={{ value: '-1SD', fontSize: 9, fill: '#D97706' }} />
                    <ReferenceLine y={mean + 2 * sd} stroke="#EA580C" strokeDasharray="4 2" label={{ value: '+2SD', fontSize: 9, fill: '#EA580C' }} />
                    <ReferenceLine y={mean - 2 * sd} stroke="#EA580C" strokeDasharray="4 2" label={{ value: '-2SD', fontSize: 9, fill: '#EA580C' }} />
                    <ReferenceLine y={mean + 3 * sd} stroke="#DC2626" label={{ value: '+3SD', fontSize: 9, fill: '#DC2626' }} />
                    <ReferenceLine y={mean - 3 * sd} stroke="#DC2626" label={{ value: '-3SD', fontSize: 9, fill: '#DC2626' }} />
                    <Line type="monotone" dataKey="value" stroke="#045EB8" strokeWidth={2}
                        dot={(props: any) => {
                            const { cx, cy, payload, index } = props;
                            return <circle key={`dot-${index}`} cx={cx} cy={cy} r={4} fill={payload.outOfControl ? '#DC2626' : '#045EB8'} stroke="#fff" strokeWidth={1} />;
                        }} />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
};

// ─── Main QualityControl ─────────────────────────────────────────────────────────
export const QualityControl: React.FC = () => {
    const { currentUser } = useLabPermissions();
    const {
        instruments, addInstrument, deleteInstrument,
        samples, addQCSample, deleteQCSample,
        runs, addRun, deleteRun,
    } = useQCData();

    const [selectedInstrumentId, setSelectedInstrumentId] = useState<string | null>(null);
    const [selectedSampleId, setSelectedSampleId] = useState<string | null>(null);
    const [showAddInstrument, setShowAddInstrument] = useState(false);
    const [showAddSample, setShowAddSample] = useState(false);
    const [runValue, setRunValue] = useState('');

    const instrumentSamples = samples.filter(s => s.instrumentId === selectedInstrumentId);
    const selectedSample = samples.find(s => s.id === selectedSampleId) ?? null;
    const sampleRuns = runs.filter(r => r.qcSampleId === selectedSampleId);

    const evaluations = useMemo(() => {
        if (!selectedSample) return [];
        return evaluateWestgardRules(sampleRuns, selectedSample.targetMean, selectedSample.targetSD);
    }, [selectedSample, sampleRuns]);

    const violatingEvaluations = evaluations.filter(e => e.violations.length > 0).slice().reverse();

    const handleAddRun = () => {
        if (!selectedSample || runValue === '') return;
        addRun({
            qcSampleId: selectedSample.id,
            value: Number(runValue),
            operator: currentUser?.name ?? currentUser?.id ?? 'Unknown user',
            timestamp: new Date().toISOString(),
        });
        setRunValue('');
    };

    return (
        <div className="flex flex-col gap-4 v2-animate-fade-in-up">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex flex-col gap-0.5">
                    <h1 className="text-[18px] font-black text-[var(--v2-text-heading)] m-0 tracking-tight">Quality Control</h1>
                    <p className="text-[11px] text-[var(--v2-text-muted)] m-0">Instruments, QC runs, Westgard rules & Levey-Jennings charts</p>
                </div>
            </div>

            <div className="flex items-start gap-2 rounded-xl px-4 py-3" style={{ background: 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.2)' }}>
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#D97706' }} strokeWidth={2} />
                <p className="text-[11px] m-0" style={{ color: 'var(--v2-text-secondary)' }}>
                    Saved on this device only — not yet synced to other devices or users. There is no backend record for instruments or QC runs today.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[240px_240px_1fr] gap-4"
                style={{ minHeight: 480 }}
            >

                {/* Instruments */}
                <div className="rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] overflow-hidden flex flex-col" style={{ boxShadow: 'var(--v2-shadow-card)' }}>
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--v2-border-subtle)]">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)] m-0">Instruments</p>
                        <button type="button" onClick={() => setShowAddInstrument(true)} className="w-6 h-6 rounded-md flex items-center justify-center cursor-pointer border-0 bg-transparent text-[#059669] hover:bg-[rgba(5,150,105,0.1)]">
                            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {instruments.length === 0 && <p className="px-4 py-6 text-[11px] text-[var(--v2-text-muted)] text-center">No instruments yet</p>}
                        {instruments.map(inst => (
                            <div key={inst.id} onClick={() => { setSelectedInstrumentId(inst.id); setSelectedSampleId(null); }}
                                className="flex items-center justify-between px-4 py-2.5 cursor-pointer border-b border-[var(--v2-border-divider)] last:border-0 transition-colors"
                                style={{ background: selectedInstrumentId === inst.id ? 'var(--v2-bg-surface)' : 'transparent' }}>
                                <div className="flex items-center gap-2 min-w-0">
                                    <Activity className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#045EB8' }} strokeWidth={2} />
                                    <div className="min-w-0">
                                        <p className="text-[11px] font-bold text-[var(--v2-text-primary)] m-0 truncate">{inst.name}</p>
                                        {inst.department && <p className="text-[9px] text-[var(--v2-text-muted)] m-0">{inst.department}</p>}
                                    </div>
                                </div>
                                <button type="button" onClick={e => { e.stopPropagation(); deleteInstrument(inst.id); if (selectedInstrumentId === inst.id) setSelectedInstrumentId(null); }}
                                    className="w-5 h-5 rounded flex items-center justify-center cursor-pointer border-0 bg-transparent text-red-400 hover:text-red-600 flex-shrink-0">
                                    <Trash2 className="w-3 h-3" strokeWidth={2} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* QC Samples / Levels */}
                <div className="rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] overflow-hidden flex flex-col" style={{ boxShadow: 'var(--v2-shadow-card)' }}>
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--v2-border-subtle)]">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)] m-0">QC Samples</p>
                        {selectedInstrumentId && (
                            <button type="button" onClick={() => setShowAddSample(true)} className="w-6 h-6 rounded-md flex items-center justify-center cursor-pointer border-0 bg-transparent text-[#059669] hover:bg-[rgba(5,150,105,0.1)]">
                                <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
                            </button>
                        )}
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {!selectedInstrumentId && <p className="px-4 py-6 text-[11px] text-[var(--v2-text-muted)] text-center">Select an instrument</p>}
                        {selectedInstrumentId && instrumentSamples.length === 0 && <p className="px-4 py-6 text-[11px] text-[var(--v2-text-muted)] text-center">No QC samples yet</p>}
                        {instrumentSamples.map(s => (
                            <div key={s.id} onClick={() => setSelectedSampleId(s.id)}
                                className="flex items-center justify-between px-4 py-2.5 cursor-pointer border-b border-[var(--v2-border-divider)] last:border-0 transition-colors"
                                style={{ background: selectedSampleId === s.id ? 'var(--v2-bg-surface)' : 'transparent' }}>
                                <div className="flex items-center gap-2 min-w-0">
                                    <Beaker className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#7C3AED' }} strokeWidth={2} />
                                    <div className="min-w-0">
                                        <p className="text-[11px] font-bold text-[var(--v2-text-primary)] m-0 truncate">{s.testName}</p>
                                        <p className="text-[9px] text-[var(--v2-text-muted)] m-0">{s.level} · μ={s.targetMean} σ={s.targetSD}</p>
                                    </div>
                                </div>
                                <button type="button" onClick={e => { e.stopPropagation(); deleteQCSample(s.id); if (selectedSampleId === s.id) setSelectedSampleId(null); }}
                                    className="w-5 h-5 rounded flex items-center justify-center cursor-pointer border-0 bg-transparent text-red-400 hover:text-red-600 flex-shrink-0">
                                    <Trash2 className="w-3 h-3" strokeWidth={2} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Chart + log */}
                <div className="rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] overflow-hidden flex flex-col" style={{ boxShadow: 'var(--v2-shadow-card)' }}>
                    {!selectedSample ? (
                        <div className="flex-1 flex items-center justify-center">
                            <p className="text-[12px] text-[var(--v2-text-muted)]">Select a QC sample to enter runs and view the Levey-Jennings chart</p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-4 p-4">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div>
                                    <p className="text-[13px] font-bold text-[var(--v2-text-heading)] m-0">{selectedSample.testName} — {selectedSample.level}</p>
                                    <p className="text-[10px] text-[var(--v2-text-muted)] m-0">Target mean {selectedSample.targetMean}{selectedSample.unit ? ` ${selectedSample.unit}` : ''} · SD {selectedSample.targetSD}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input type="number" className={`${inputCls} w-28`} placeholder="Value" value={runValue} onChange={e => setRunValue(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddRun()} />
                                    <button type="button" onClick={handleAddRun} disabled={runValue === ''}
                                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-bold cursor-pointer text-white disabled:opacity-50 border-0" style={{ background: '#059669' }}>
                                        <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Log Run
                                    </button>
                                </div>
                            </div>

                            {evaluations.length === 0 ? (
                                <p className="text-[11px] text-[var(--v2-text-muted)] text-center py-8">No QC runs logged yet</p>
                            ) : (
                                <LeveyJenningsChart sample={selectedSample} evaluations={evaluations} />
                            )}

                            {/* Violations */}
                            {violatingEvaluations.length > 0 && (
                                <div className="rounded-xl px-3 py-2.5 flex flex-col gap-1.5" style={{ background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)' }}>
                                    <p className="text-[10px] font-bold uppercase tracking-wider m-0" style={{ color: '#DC2626' }}>Rule Violations</p>
                                    {violatingEvaluations.slice(0, 5).map(e => (
                                        <div key={e.run.id} className="flex items-center justify-between gap-2 text-[11px]">
                                            <span className="text-[var(--v2-text-secondary)]">{new Date(e.run.timestamp).toLocaleString('en-IN')} — value {e.run.value} (z={e.zScore.toFixed(2)})</span>
                                            <div className="flex items-center gap-1 flex-wrap justify-end">
                                                {e.violations.map(v => (
                                                    <span key={v} className="text-[9px] font-black px-1.5 py-0.5 rounded-full"
                                                        style={{
                                                            background: REJECTING_RULES.includes(v) ? 'rgba(220,38,38,0.12)' : 'rgba(217,119,6,0.12)',
                                                            color: REJECTING_RULES.includes(v) ? '#DC2626' : '#D97706',
                                                        }}>
                                                        {WESTGARD_RULE_LABELS[v]}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* QC Log */}
                            <div className="rounded-xl border border-[var(--v2-border-card)] overflow-hidden">
                                <table className="w-full text-[11px]">
                                    <thead>
                                        <tr style={{ background: 'var(--v2-bg-surface)' }}>
                                            {['Date', 'Value', 'Z-Score', 'Operator', 'Status', ''].map(h => (
                                                <th key={h} className="text-left px-3 py-2 font-bold text-[var(--v2-text-muted)] text-[9px] uppercase tracking-wider">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {evaluations.slice().reverse().map(e => (
                                            <tr key={e.run.id} className="border-t border-[var(--v2-border-divider)]">
                                                <td className="px-3 py-2 text-[var(--v2-text-secondary)]">{new Date(e.run.timestamp).toLocaleString('en-IN')}</td>
                                                <td className="px-3 py-2 font-semibold text-[var(--v2-text-primary)]">{e.run.value}</td>
                                                <td className="px-3 py-2 text-[var(--v2-text-muted)]">{e.zScore.toFixed(2)}</td>
                                                <td className="px-3 py-2 text-[var(--v2-text-muted)]">{e.run.operator}</td>
                                                <td className="px-3 py-2">
                                                    {e.outOfControl
                                                        ? <span className="inline-flex items-center gap-1 text-[9px] font-black px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(220,38,38,0.1)', color: '#DC2626' }}><AlertTriangle className="w-2.5 h-2.5" strokeWidth={2.5} /> Reject</span>
                                                        : e.violations.length > 0
                                                            ? <span className="inline-flex items-center gap-1 text-[9px] font-black px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(217,119,6,0.1)', color: '#D97706' }}>Warning</span>
                                                            : <span className="inline-flex items-center gap-1 text-[9px] font-black px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(5,150,105,0.1)', color: '#059669' }}><CheckCircle2 className="w-2.5 h-2.5" strokeWidth={2.5} /> In Control</span>
                                                    }
                                                </td>
                                                <td className="px-3 py-2">
                                                    <button type="button" onClick={() => deleteRun(e.run.id)} className="w-5 h-5 rounded flex items-center justify-center cursor-pointer border-0 bg-transparent text-red-400 hover:text-red-600">
                                                        <Trash2 className="w-3 h-3" strokeWidth={2} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {showAddInstrument && (
                <AddInstrumentModal onClose={() => setShowAddInstrument(false)} onSave={data => { addInstrument(data); setShowAddInstrument(false); }} />
            )}
            {showAddSample && selectedInstrumentId && (
                <AddQCSampleModal onClose={() => setShowAddSample(false)} onSave={data => { addQCSample({ ...data, instrumentId: selectedInstrumentId }); setShowAddSample(false); }} />
            )}
        </div>
    );
};

export default QualityControl;
